import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Server, Monitor, Check } from 'lucide-react';

function SolutionGroup({ title, items }) {
  return (
    <div className="space-y-2">
      <p className="font-display font-semibold text-sm text-foreground">{title}</p>
      <ul className="space-y-1.5">
        {items.map(item => (
          <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
            <Check className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Services() {
  const audiocodesSolutions = [
    { title: 'Conectividad y Seguridad de Voz', items: ['Session Border Controllers (SBC)', 'Media Gateways', 'SIP Trunking y protección de voz'] },
    { title: 'Microsoft Teams', items: ['Direct Routing', 'Operator Connect', 'Survivable Branch Appliance (SBA)', 'Gestión y monitoreo'] },
    { title: 'Contact Center y Experiencia del Cliente', items: ['Voca Contact Center', 'IVR Inteligente', 'Grabación y analítica'] },
    { title: 'Dispositivos y Endpoints', items: ['Teléfonos IP', 'Adaptadores ATA'] },
  ];

  const audiocodesServices = [
    'Diseño y dimensionamiento de soluciones',
    'Implementación y puesta en marcha',
    'Migraciones y actualizaciones',
    'Capacitación técnica certificada',
    'Soporte 24x7',
    'Servicios administrados',
    'Soporte remoto y en sitio',
  ];

  const onescreenSolutions = [
    { title: 'Colaboración Empresarial', items: ['Salas de juntas inteligentes', 'Videoconferencia integrada', 'Presentación inalámbrica'] },
    { title: 'Educación y Capacitación', items: ['Aulas digitales', 'Capacitación corporativa', 'Pizarras interactivas'] },
    { title: 'Trabajo Híbrido', items: ['Colaboración remota', 'Compartición de contenido', 'Integración con Teams y Zoom'] },
  ];

  const onescreenServices = [
    'Diseño y equipamiento de salas de colaboración',
    'Instalación y puesta en marcha',
    'Integración con Microsoft Teams y Zoom',
    'Capacitación para usuarios y administradores',
    'Soporte técnico especializado',
    'Garantía y servicios postventa',
  ];

  return (
    <div className="py-16 md:py-20" data-testid="services-page">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <Badge variant="secondary" className="mb-4">Servicios</Badge>
          <h1 className="text-4xl sm:text-5xl font-display font-semibold tracking-tight mb-4" data-testid="services-title">Soluciones y Servicios</h1>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed" data-testid="services-intro">
            Distribuimos, implementamos y damos soporte a soluciones de comunicaciones empresariales y de operador. Nuestro equipo certificado acompaña a los clientes desde el diseño de la solución hasta la operación y soporte continuo.
          </p>
        </div>

        <Tabs defaultValue="audiocodes" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8 max-w-md">
            <TabsTrigger value="audiocodes" className="gap-2" data-testid="services-tab-audiocodes"><Server className="h-4 w-4" /> AudioCodes</TabsTrigger>
            <TabsTrigger value="onescreen" className="gap-2" data-testid="services-tab-onescreen"><Monitor className="h-4 w-4" /> OneScreen</TabsTrigger>
          </TabsList>

          {/* AudioCodes */}
          <TabsContent value="audiocodes">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-center justify-center p-8 bg-secondary/30 rounded-lg">
                  <img src="/logo-audiocodes.png" alt="AudioCodes" className="h-24 w-auto object-contain" />
                </div>
                <Card>
                  <CardContent className="p-6 space-y-4">
                    <h3 className="font-display font-semibold text-lg">Servicios Profesionales</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {audiocodesServices.map(s => (
                        <li key={s} className="flex items-start gap-2"><Check className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />{s}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
              <div className="space-y-6">
                <h2 className="text-2xl font-display font-semibold" data-testid="services-audiocodes-solutions-title">Soluciones AudioCodes</h2>
                <div className="space-y-6">
                  {audiocodesSolutions.map(group => (
                    <SolutionGroup key={group.title} title={group.title} items={group.items} />
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* OneScreen */}
          <TabsContent value="onescreen">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-center justify-center p-8 bg-secondary/30 rounded-lg">
                  <img src="/logo-onescreen.png" alt="OneScreen" className="h-24 w-auto object-contain" />
                </div>
                <Card>
                  <CardContent className="p-6 space-y-4">
                    <h3 className="font-display font-semibold text-lg">Servicios Profesionales</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {onescreenServices.map(s => (
                        <li key={s} className="flex items-start gap-2"><Check className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />{s}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
              <div className="space-y-6">
                <h2 className="text-2xl font-display font-semibold" data-testid="services-onescreen-solutions-title">Soluciones OneScreen</h2>
                <div className="space-y-6">
                  {onescreenSolutions.map(group => (
                    <SolutionGroup key={group.title} title={group.title} items={group.items} />
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
